import {
  CalendarOutlined,
  CheckCircleFilled,
  DownloadOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAppAccessTypeLink } from "../../../common/AccessTypeLinks";
import { AlertMessage } from "../../../common/data/AlertMsg";
import { ModalMessage } from "../../../common/data/ModalMsg";
import { TooltipMessage } from "../../../common/data/TooltipMsg";
import { isNullOrEmptyString, isNullOrUndefinedOrEmpty } from "../../../common/utils";
import { differenceInDays } from "../../../common/utils/TimeUtils";
import { AsyncActionButtonOrLink } from "../../../components/asyncActionButtonOrLink";
import { LinkSpan } from "../../../components/linkSpan";
import {
  ApplicationAction,
  ReviewAction,
  sendApplicationEvent,
  sendReviewEvent,
} from "../../../components/matomoClient";
import { errorMsg } from "../../../components/simpleMessageAlert";
import { IsFeatureEnabledOrNot } from "../../../FeatureToggles/FeatureEnabledOrNot";
import { ToggleList } from "../../../FeatureToggles/toggleList";
import {
  ApplicationInReviewStatus,
  ApplicationListPageActions,
  complianceTags,
  ReviewVerificationPending,
} from "../../../pages/applications/applicationList/model";
import { reviewTab } from "../../../pages/applications/applicationDetail/model";
import { cancelReview, previewDataBeforeStartReview } from "../../../pages/applications/applicationDetail/service";
import {
  userAccessColumnGenerator
} from "../../../pages/applications/applicationDetail/tabs/static/userAccess/columnGenerator";
import "./index.css";
import { CloseReviewModalContent, ITGCCloseReviewBtn } from "./closeITGCAppReviewBtn";
import { useQuery } from "../../../common/hooks";
import { Routes } from "../../../common/Routes";
import { v4 as uuidv4 } from 'uuid';
import { updateAccessTypeLink } from "../../../pages/applications/applicationList/service";
import { fetchUserRoles } from "../../../pages/users/userDetail/service";
import { FieldEdit } from "../../../components/FieldEdit";

function GetUserAccessBtn({ showGetUserDataBtn, appInfo, appId, sendGetUserAccessDataEmail }) {
  let btn = null;

  if (showGetUserDataBtn) {
    let okCallback = async () => {
      sendApplicationEvent(ApplicationAction.RequestUserAccess, appInfo.name);
      await sendGetUserAccessDataEmail(appId, appInfo.gitsProductTeam.email);
    };

    btn = (
      <AsyncActionButtonOrLink
        type="button"
        tooltipText={TooltipMessage.requestUserAccessData}
        buttonProps={{
          icon: <DownloadOutlined />,
          text: "Request User Access Data",
          authZButtonType: "default",
        }}
        modalProps={{
          content: ModalMessage.requestUserAccessData.getConfirmationMsg(
            appInfo?.gitsProductTeam?.email
          ),
          title: ModalMessage.requestUserAccessData.title,
          className: "start-modal",
        }}
        modalOkButtonProps={{
          okCallback: okCallback,
          okText: ModalMessage.requestUserAccessData.okText,
        }}
      />
    );
  }
  return btn;
}

function GetCloseReviewBtn({
  appId,
  appInfo,
  showReviewBtn,
  closeReview,
  refreshAppDetail,
  setActiveTab,
  closeActiveReview,
  refreshReviewHistory,
  autoClick = false,
  latestReviewId
}) {
  let btn = null;

  const [comment, setComment] = useState("");
  const [disabled, setDisabled] = useState(true);

  const [refreshTicket, setRefreshTicket] = useState("")
  const [loading, setLoading] = useState(true);

  const closeReviewBtnCallback = () => {
    setRefreshTicket(uuidv4())
  }

  if (showReviewBtn) {
    let okCallback = async () => {
      await closeReview(appId, comment);
      refreshAppDetail();
      setActiveTab(reviewTab.id);
      closeActiveReview();
      refreshReviewHistory();
      sendReviewEvent(ReviewAction.CloseReview, appInfo.name);
    };

      let content = (
        <CloseReviewModalContent
          setDisabled={setDisabled}
          setComment={setComment}
          refreshTicket={refreshTicket}
          loading={loading}
          setLoading={setLoading}
          latestReviewId={latestReviewId}
          isItgc={false}
        />
      );

    const cancelFn = () => {
      setLoading(true)
    }

    btn = (
      <AsyncActionButtonOrLink
        type="button"
        tooltipText={TooltipMessage.closeReview}
        buttonProps={{
          icon: <CalendarOutlined />,
          text: "Close Review",
          authZButtonType: "primary",
          onButtonClick: closeReviewBtnCallback
        }}
        modalProps={{
          content: content,
          title: ModalMessage.closeReview.title,
          className: "start-modal",
        }}
        modalOkButtonProps={{
          okCallback: okCallback,
          okText: ModalMessage.closeReview.okText,
          disabled: disabled
        }}
        modalCancelButtonProps={{
          cancelCallBack: cancelFn
        }}
        displayModal={autoClick}
      />
    );
  }
  return btn;
}

function GetStartReviewBtn({
  appId,
  refreshAppDetail,
  setActiveTab,
  hasUserAccess,
  isLastImportedGreaterThan7Days,
  showReviewBtn,
  startReview,
  refreshReviewHistory,
  openActiveReviewTab,
  appInfo,
  isUserAccessDataDumpAvailable,
  isLastImportedFeatureEnabled,
  isInVerificationPending,
  autoClick = false
}) {
  const [previewDataForReview, setPreviewDataForReview] = useState({});

  let btn = null;

  const disabled =
    !hasUserAccess ||
    isUserAccessDataDumpAvailable === false ||
    isInVerificationPending ||
    (isLastImportedFeatureEnabled && isLastImportedGreaterThan7Days);

  if (showReviewBtn) {
    let okCallback = async () => {
      await startReview(appId);
      refreshAppDetail();
      setActiveTab(reviewTab.id);
      refreshReviewHistory();
      openActiveReviewTab();
      sendReviewEvent(ReviewAction.StartReview, appInfo.name);
    };

    async function buttonClickCallback() {
      try {
        setPreviewDataForReview(await previewDataBeforeStartReview(appId));
        sendReviewEvent(ReviewAction.PreviewBeforeStartReview, appInfo.name);
      } catch (err) {
        errorMsg(AlertMessage.previewBeforeStartReview.error);
        throw err;
      }
    }

    function cancelCallBack() {
      setPreviewDataForReview({});
    }

    const tooltipMessage = isInVerificationPending
      ? TooltipMessage.startReview.disabled.inReviewVerificationPending
      : isLastImportedFeatureEnabled === false
      ? TooltipMessage.startReview.enabled
      : isUserAccessDataDumpAvailable === false
      ? TooltipMessage.startReview.disabled.noUserAccessDataDump
      : isLastImportedGreaterThan7Days === true
      ? TooltipMessage.startReview.disabled.moreThanWeek
      : hasUserAccess === false
      ? TooltipMessage.startReview.disabled.noReviewee
      : TooltipMessage.startReview.enabled;

    let dataSource = previewDataForReview.dataSource;
    let columns = previewDataForReview.columns;

    const rowClassName = (record) => {
      if (isNullOrEmptyString(record.delegatedTo)) {
        return "table-row-no-delegation";
      }
    };

    columns = userAccessColumnGenerator(columns);

    const noOfReviewTaskWithDelegates = dataSource?.filter(
      (review) => !isNullOrEmptyString(review.delegatedTo)
    ).length;

    btn = (
      <AsyncActionButtonOrLink
        type="button"
        tooltipText={tooltipMessage}
        buttonProps={{
          icon: <CalendarOutlined />,
          text: "Start Review",
          disabled: disabled,
          authZButtonType: "primary",
          onButtonClick: buttonClickCallback,
        }}
        modalProps={{
          content: ModalMessage.startReview.getConfirmationMsg(
            noOfReviewTaskWithDelegates,
            dataSource?.length
          ),
          title: ModalMessage.startReview.title,
          dataSource: dataSource,
          columns: columns,
          rowClassName: rowClassName,
          modalType: "preview",
        }}
        modalOkButtonProps={{
          okCallback: okCallback,
          okText: ModalMessage.startReview.okText,
        }}
        modalCancelButtonProps={{
          cancelCallBack: cancelCallBack,
        }}
        displayModal={autoClick && !disabled}
        showLoadingOverlay={true}
      />
    );
  }
  return btn;
}

function GetPreReviewBtn({
  appId,
  appInfo,
  hasUserAccess,
  isLastImportedGreaterThan7Days,
  isUserAccessDataDumpAvailable,
  isLastImportedFeatureEnabled,
  isInVerificationPending,
}) {
  const [previewData, setPreviewData] = useState({});

  async function buttonClickCallback() {
    try {
      setPreviewData(await previewDataBeforeStartReview(appId));
      sendReviewEvent(ReviewAction.PreviewBeforeStartReview, appInfo.name);
    } catch (err) {
      errorMsg(AlertMessage.previewBeforeStartReview.error);
      throw err;
    }
  }

  function cancelCallBack() {
    setPreviewData({});
  }

  const disabled =
    !hasUserAccess ||
    isUserAccessDataDumpAvailable === false ||
    isInVerificationPending ||
    (isLastImportedFeatureEnabled && isLastImportedGreaterThan7Days);

  const tooltipMessage = isInVerificationPending
    ? TooltipMessage.startReview.disabled.inReviewVerificationPending
    : isLastImportedFeatureEnabled === false
    ? TooltipMessage.preview
    : isUserAccessDataDumpAvailable === false
    ? TooltipMessage.startReview.disabled.noUserAccessDataDump
    : isLastImportedGreaterThan7Days === true
    ? TooltipMessage.startReview.disabled.moreThanWeek
    : hasUserAccess === false
    ? TooltipMessage.startReview.disabled.noReviewee
    : TooltipMessage.preview;

  let dataSource = previewData.dataSource;
  let columns = previewData.columns;

  const rowClassName = (record) => {
    if (isNullOrEmptyString(record.delegatedTo)) {
      return "table-row-no-delegation";
    }
  };

  columns = userAccessColumnGenerator(columns);

  const noOfReviewTaskWithDelegates = dataSource?.filter(
    (review) => !isNullOrEmptyString(review.delegatedTo)
  ).length;

  return (
    <AsyncActionButtonOrLink
      type="button"
      tooltipText={tooltipMessage}
      buttonProps={{
        icon: <CalendarOutlined />,
        text: "Preview",
        disabled: disabled,
        authZButtonType: "primary",
        onButtonClick: buttonClickCallback,
      }}
      modalProps={{
        content: ModalMessage.startReview.getConfirmationMsg(
          noOfReviewTaskWithDelegates,
          dataSource?.length
        ),
        title: ModalMessage.preview.title,
        dataSource: dataSource,
        columns: columns,
        rowClassName: rowClassName,
        modalType: "preview",
      }}
      modalOkButtonProps={{
        disabled: true,
      }}
      modalCancelButtonProps={{
        cancelCallBack: cancelCallBack,
      }}
    />
  );
}

function ShowStartOrClosedReviewButton({
  isInReview,
  isInVerificationPending,
  appId,
  appInfo,
  showReviewBtn,
  closeReview,
  refreshAppDetail,
  setActiveTab,
  closeActiveReview,
  refreshReviewHistory,
  hasUserAccess,
  isLastImportedGreaterThan7Days,
  startReview,
  openActiveReviewTab,
  isLastImportedFeatureEnabled,
  isUserAccessDataDumpAvailable,
  getReviewTicket,
  latestReviewId
}) {
  const query = useQuery();
  const toStartReview = query.get('startReview') === "yes";
  const toCloseReview = query.get('closeReview') === "yes";
  useEffect(() => {
    if (toStartReview || toCloseReview)
      window.history.replaceState({}, "", Routes.Applications.path + "/" + appInfo.uniqueName);
  }, []);

  const isITGCApp = appInfo?.complianceTags.includes(complianceTags.itgc);
  return (
    <div className={"review-action-btn"}>
      {isInReview ? (
        isITGCApp ? (
          <ITGCCloseReviewBtn
            appId={appId}
            appInfo={appInfo}
            showReviewBtn={showReviewBtn}
            closeReview={closeReview}
            refreshAppDetail={refreshAppDetail}
            setActiveTab={setActiveTab}
            closeActiveReview={closeActiveReview}
            refreshReviewHistory={refreshReviewHistory}
            getReviewTicket={getReviewTicket}
            autoClick={toCloseReview}
            latestReviewId={latestReviewId}
          />
        ) : (
          <GetCloseReviewBtn
            appId={appId}
            appInfo={appInfo}
            showReviewBtn={showReviewBtn}
            closeReview={closeReview}
            refreshAppDetail={refreshAppDetail}
            setActiveTab={setActiveTab}
            closeActiveReview={closeActiveReview}
            refreshReviewHistory={refreshReviewHistory}
            autoClick={toCloseReview}
            latestReviewId={latestReviewId}
          />
        )
      ) : (
        <GetStartReviewBtn
          hasUserAccess={hasUserAccess}
          isLastImportedGreaterThan7Days={isLastImportedGreaterThan7Days}
          appId={appId}
          refreshAppDetail={refreshAppDetail}
          setActiveTab={setActiveTab}
          showReviewBtn={showReviewBtn}
          startReview={startReview}
          refreshReviewHistory={refreshReviewHistory}
          openActiveReviewTab={openActiveReviewTab}
          appInfo={appInfo}
          isUserAccessDataDumpAvailable={isUserAccessDataDumpAvailable}
          isLastImportedFeatureEnabled={isLastImportedFeatureEnabled}
          isInVerificationPending={isInVerificationPending}
          autoClick={toStartReview}
        />
      )}
    </div>
  );
}

function ShowPreReivewButton({
  appId,
  appInfo,
  isInReview,
  isInVerificationPending,
  hasUserAccess,
  isLastImportedGreaterThan7Days,
  isUserAccessDataDumpAvailable,
  isLastImportedFeatureEnabled,
}) {
  const isPreReviewable = appInfo?.isPreReviewable;
  return isPreReviewable && !isInReview ? (
    <GetPreReviewBtn
      hasUserAccess={hasUserAccess}
      isLastImportedGreaterThan7Days={isLastImportedGreaterThan7Days}
      appId={appId}
      appInfo={appInfo}
      isUserAccessDataDumpAvailable={isUserAccessDataDumpAvailable}
      isLastImportedFeatureEnabled={isLastImportedFeatureEnabled}
      isInVerificationPending={isInVerificationPending}
    />
  ) : null;
}

function ShowCancelReviewButton({
  appId,
  refreshAppDetail,
  setActiveTab,
  closeActiveReview,
  refreshReviewHistory,
  appInfo,
  getReviewTicket,
  latestReviewId
}) {
  const [comment, setComment] = useState("");
  const [disabled, setDisabled] = useState(true);
  const [refreshTicket, setRefreshTicket] = useState("")
  const [loading, setLoading] = useState(true);
  const query = useQuery();
  const toCancelReview = query.get('cancelReview') === "yes";
  useEffect(() => {
    if (toCancelReview)
      window.history.replaceState({}, "", Routes.Applications.path + "/" + appInfo.uniqueName);
  }, []);
  const cancelReviewBtnCallback = () => {
    setRefreshTicket(uuidv4())
  }
  const okFn = async () => {
    if (isNullOrEmptyString(comment))
      return;

    await cancelReview(appId, comment);
    refreshAppDetail();
    setActiveTab(reviewTab.id);
    closeActiveReview();
    refreshReviewHistory();
    sendReviewEvent(ReviewAction.CancelReview, appInfo.name);
  };

  const cancelFn = () => {
    setLoading(true)
  }

  let content = (
    <CloseReviewModalContent
      getReviewTicket={getReviewTicket}
      setDisabled={setDisabled}
      setComment={setComment}
      refreshTicket={refreshTicket}
      loading={loading}
      setLoading={setLoading}
      latestReviewId={latestReviewId}
      isCancelPopup={true}
      comment={comment}
    />
  );
  return (
    <div className={"review-action-btn"}>
    <AsyncActionButtonOrLink
      type="button"
      tooltipText={TooltipMessage.cancelReview}
      buttonProps={{
        icon: <CalendarOutlined />,
        text: "Cancel Review",
        authZButtonType: "primary",
        ariaLabel: "cancel-review",
        onButtonClick: cancelReviewBtnCallback
      }}
      modalProps={{
        content: content,
        title: ModalMessage.cancelReview.title,
        className: "start-modal",
        modalType: "closeReview",
      }}
      modalOkButtonProps={{
        okCallback: okFn,
        okText: ModalMessage.cancelReview.okText,
        disabled: disabled,
      }}
      modalCancelButtonProps={{
        cancelCallBack: cancelFn
      }}
      displayModal={toCancelReview}
    />
    </div>
  );
}

export const ApplicationReviewActionBar = (props) => {
  const {
    appInfo,
    appId,
    refreshAppDetail,
    showGetUserDataBtn,
    showReviewBtn,
    sendGetUserAccessDataEmail,
    startReview,
    closeReview,
    refreshReviewHistory,
    setActiveTab,
    openActiveReviewTab,
    closeActiveReview,
    showStatusAsNotReviewable,
    getReviewTicket,
    latestReviewId
  } = props;

  const isInReview = appInfo?.recentReviews?.inProgressReview?.status === ApplicationInReviewStatus;
  const isInVerificationPending =
    appInfo?.recentReviews?.lastClosedReview?.status === ReviewVerificationPending;
  const isReviewable = appInfo?.isReviewable??false;
  const isScreenshotAccessible = appInfo?.isGdriveViewable;
  const hasUserAccess = appInfo?.totalReviewee > 0;
  const isUserAccessDataDumpAvailable =
    appInfo?.lastImportedOn !== undefined && !isNullOrEmptyString(appInfo?.lastImportedOn);
  const isLastImportedGreaterThan7Days = isUserAccessDataDumpAvailable
    ? differenceInDays(new Date(), new Date(appInfo?.lastImportedOn)) > 7
    : false;
  const isLastImportedFeatureEnabled = IsFeatureEnabledOrNot(ToggleList.LastImported);
  const isReviewCycleFeatureToggleEnabled = IsFeatureEnabledOrNot(ToggleList.ReviewCycle);

  function showReviewStatusText() {
    let lastReviewOnText = "Not reviewed yet";
    if (
      showStatusAsNotReviewable ||
      (appInfo?.isReviewable === false &&
        !isNullOrUndefinedOrEmpty(appInfo?.recentReviews?.inProgressReview) &&
        isNullOrUndefinedOrEmpty(appInfo?.recentReviews?.lastClosedReview))
    ) {
      lastReviewOnText = "Not reviewable";
    } else if (appInfo?.recentReviews?.lastClosedReview?.endDate) {
      lastReviewOnText = `Last reviewed on ${appInfo?.recentReviews?.lastClosedReview?.endDate}`;
    }
    return lastReviewOnText;
  }

  const lastImportedDate = () => {
    let message = "Not uploaded yet";
    if (appInfo && appInfo.lastImportedOn) {
      message = `Last Imported on ${appInfo.lastImportedOn}`;
    }
    return message;
  };

  function screenshotIcon() {
    return (
      <div className="icon-padding">
        <Tooltip placement="top" title="Link to screenshots">
          <Link to={{ pathname: appInfo.accessDataDriveLink }} target="_blank">
            <PictureOutlined className="link-to-screenshot" />
          </Link>
        </Tooltip>
      </div>
    );
  }

  const [canEditAccessTypeLink, setCanEditAccessTypeLink] = useState(false)
  useEffect(() => {
    if (appId === undefined) return
    fetchUserRoles(appId).then(response => {
      if (response.globalAccessApprover || response.authZMember || response.gitsTeam) {
        setCanEditAccessTypeLink(true)
      }
    })
  }, [appId])

  return (
    <div className={"reviewInfo"}>
      <div>
        <CheckCircleFilled style={{ color: "#619faa", width: 18, height: 18 }} />
        <span className={"lastReviewDate"}>
          {showReviewStatusText()}
          &nbsp;|&nbsp;
          <LinkSpan
            text={"View Access Types"}
            target="_blank"
            to={{ pathname: getAppAccessTypeLink(appInfo)?.url }}
            matomoContext={appInfo?.name}
            active={getAppAccessTypeLink(appInfo)?.active}
          />
          {(canEditAccessTypeLink) ? <AccessTypeLinkUpdate appId={appId} appInfo={appInfo}/> : null}
        </span>
      </div>
      <div className={"accessApproverAction"}>
        <span className={"lastImportedDate"}>{lastImportedDate()}</span>
        {isScreenshotAccessible && isReviewable && !isReviewCycleFeatureToggleEnabled
          ? screenshotIcon()
          : null}
        {
          !isReviewable ?
              <GetUserAccessBtn
                  showGetUserDataBtn={showGetUserDataBtn}
                  appInfo={appInfo}
                  appId={appId}
                  sendGetUserAccessDataEmail={sendGetUserAccessDataEmail}
              />
              : null
        }
        {isReviewable === false ? null : (
          <ShowStartOrClosedReviewButton
            isInReview={isInReview}
            isInVerificationPending={isInVerificationPending}
            appId={appId}
            appInfo={appInfo}
            showReviewBtn={showReviewBtn}
            closeReview={closeReview}
            refreshAppDetail={refreshAppDetail}
            setActiveTab={setActiveTab}
            closeActiveReview={closeActiveReview}
            refreshReviewHistory={refreshReviewHistory}
            hasUserAccess={hasUserAccess}
            isLastImportedGreaterThan7Days={isLastImportedGreaterThan7Days}
            startReview={startReview}
            openActiveReviewTab={openActiveReviewTab}
            isLastImportedFeatureEnabled={isLastImportedFeatureEnabled}
            isUserAccessDataDumpAvailable={isUserAccessDataDumpAvailable}
            getReviewTicket={getReviewTicket}
            latestReviewId={latestReviewId}
          />
        )}
        {isReviewable === false ? null : (
          <ShowPreReivewButton
            appId={appId}
            appInfo={appInfo}
            isInReview={isInReview}
            isInVerificationPending={isInVerificationPending}
            hasUserAccess={hasUserAccess}
            isLastImportedGreaterThan7Days={isLastImportedGreaterThan7Days}
            isUserAccessDataDumpAvailable={isUserAccessDataDumpAvailable}
            isLastImportedFeatureEnabled={isLastImportedFeatureEnabled}
          />
        )}
        {isReviewable === true && isInReview === true ? (
          <ShowCancelReviewButton
            appId={appId}
            refreshAppDetail={refreshAppDetail}
            setActiveTab={setActiveTab}
            closeActiveReview={closeActiveReview}
            refreshReviewHistory={refreshReviewHistory}
            appInfo={appInfo}
            getReviewTicket={getReviewTicket}
            latestReviewId={latestReviewId}
          />
        )
          : null
        }
      </div>
    </div>
  );
};

function AccessTypeLinkUpdate({appId, appInfo}) {
  const isInputValid = (input) => {
    const regex = new RegExp("^https://docs\\.google\\.com.*$")
    return regex.test(input)
  }

  const inputLabel = (
    <>
      <div style={{fontSize: "16px", fontWeight: 600}}>Application access type link</div>
      <div>The access type link can be updated, but not deleted</div>
    </>
  )

  return (
    <FieldEdit
      appId={appId}
      editButtonAriaLabel={"access-link-update-button"}
      modalTitle={`${appInfo?.name} - Update Access Type Link`}
      inputLabel={inputLabel}
      inputErrorMessage={"The link should start with 'https://docs.google.com'"}
      savedValue={(appInfo?.accessTypeLink) ? appInfo.accessTypeLink : ""}
      isInputValid={isInputValid}
      updateApi={updateAccessTypeLink}
      actionAfterUpdate={ApplicationListPageActions.updateAppAccessTypeLink}
      successMessage={"Access type link successfully updated"}
      buttonTooltipMessage={"Update link"}
    />
  )
}
